import DOMPurify from 'dompurify';
import { marked } from 'marked';

marked.setOptions({
  breaks: true,
  gfm: true,
});

const isExternalHref = (href) => {
  if (!href || typeof href !== 'string') {
    return false;
  }

  return /^(https?:)?\/\//i.test(href.trim());
};

// Open external links in a new tab; harden against reverse tabnabbing.
DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  if (node.tagName !== 'A') {
    return;
  }

  const href = node.getAttribute('href') || '';
  const has_blank_target = node.getAttribute('target') === '_blank';

  if (!has_blank_target && !isExternalHref(href)) {
    return;
  }

  node.setAttribute('target', '_blank');

  const existing_rel = (node.getAttribute('rel') || '')
    .split(/\s+/)
    .filter(Boolean);
  const required_rel = ['noopener', 'noreferrer'];

  required_rel.forEach((token) => {
    if (!existing_rel.includes(token)) {
      existing_rel.push(token);
    }
  });

  node.setAttribute('rel', existing_rel.join(' '));
});

/**
 * Convert markdown to sanitized HTML safe for v-html.
 * @param {string} markdown
 * @returns {string}
 */
const renderMarkdown = (markdown) => {
  if (!markdown || typeof markdown !== 'string') {
    return '';
  }

  const html = marked.parse(markdown)
    .replace(/<table>/g, '<div class="chatbot-md-table"><table>')
    .replace(/<\/table>/g, '</table></div>');

  return DOMPurify.sanitize(html, {
    USE_PROFILES: { html: true },
    ADD_ATTR: ['target'],
  });
};

const columnExtraWidth = (cell) => {
  const style = window.getComputedStyle(cell);

  return (parseFloat(style.paddingLeft) || 0)
    + (parseFloat(style.paddingRight) || 0)
    + (parseFloat(style.borderLeftWidth) || 0)
    + (parseFloat(style.borderRightWidth) || 0);
};

const longestLineWidth = (cell) => {
  const range = document.createRange();

  range.selectNodeContents(cell);

  const widths = [...range.getClientRects()].map((rect) => rect.width);

  return widths.length ? Math.max(...widths) : 0;
};

/**
 * Shrink markdown table columns to their wrapped text.
 * CSS caps a column at half the visible width; this drops the slack
 * when the wrapped lines do not fill that cap.
 * @param {ParentNode} root
 */
const fitChatbotTableColumns = (root) => {
  if (!root || typeof root.querySelectorAll !== 'function') {
    return;
  }

  root.querySelectorAll('.chatbot-md-table').forEach((wrap) => {
    const table = wrap.querySelector('table');
    const visible_width = wrap.clientWidth;

    if (!table || !visible_width) {
      return;
    }

    const rows = [...table.rows];
    const cells = rows.flatMap((row) => [...row.cells]);

    cells.forEach((cell) => {
      cell.style.width = '';
      cell.style.minWidth = '';
      cell.style.maxWidth = '';
    });

    const widths = [];

    rows.forEach((row) => {
      let column_index = 0;

      [...row.cells].forEach((cell) => {
        const span = cell.colSpan || 1;

        if (span !== 1) {
          column_index += span;

          return;
        }

        const only_column = rows.every((item) => item.cells.length === 1);
        const cap = only_column ? visible_width : visible_width / 2;
        const fitted = Math.ceil(
          longestLineWidth(cell) + columnExtraWidth(cell) + 1
        );
        const current = Math.ceil(cell.getBoundingClientRect().width);
        const next = Math.min(current, fitted, Math.floor(cap));

        widths[column_index] = Math.max(widths[column_index] || 0, next);
        column_index += 1;
      });
    });

    rows.forEach((row) => {
      let column_index = 0;

      [...row.cells].forEach((cell) => {
        const span = cell.colSpan || 1;

        if (span !== 1) {
          column_index += span;

          return;
        }

        const width = widths[column_index];

        if (width > 0) {
          const px = `${width}px`;

          cell.style.width = px;
          cell.style.minWidth = px;
          cell.style.maxWidth = px;
        }

        column_index += 1;
      });
    });
  });
};

export {
  fitChatbotTableColumns,
  renderMarkdown,
};
